import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Pencil, RotateCcw, RotateCw, Trash2, Video, X } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import type { AisleWalkthrough } from '../types';
import {
  resolveTaggedRetailerLocations,
  type RetailerLocationOption,
} from '../lib/inStoreLocationLabels';
import {
  normalizeVideoRotationDegrees,
  nextVideoRotationDegrees,
  type VideoRotationDegrees,
} from '../lib/walkthroughVideoRotation';
import { captureWalkthroughThumbnailFromFile } from '../lib/captureWalkthroughThumbnail';
import { detectVideoRotationFromFile } from '../lib/detectWalkthroughVideoRotation';

interface WalkthroughRecordEditorProps {
  walk: AisleWalkthrough;
  storeId: number;
  storeIdParam: string;
  retailerInstoreLocations?: RetailerLocationOption[];
  onDeleted?: () => void;
  onUpdated?: () => void;
  onRotationChange?: (degrees: VideoRotationDegrees) => void;
}

const VIDEO_ACCEPT = 'video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov';

async function invalidateWalkthroughQueries(queryClient: ReturnType<typeof useQueryClient>, storeIdParam: string) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['/api/store-profiles', storeIdParam, 'context'] }),
    queryClient.invalidateQueries({ queryKey: ['/api/store-profiles', storeIdParam, 'walkthroughs'] }),
    queryClient.invalidateQueries({ queryKey: ['/api/stores', storeIdParam, 'detail'] }),
  ]);
}

export function WalkthroughRecordEditor({
  walk,
  storeId,
  storeIdParam,
  retailerInstoreLocations = [],
  onDeleted,
  onUpdated,
  onRotationChange,
}: WalkthroughRecordEditorProps) {
  const walkthroughId = walk.liveWalkthroughId;
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(walk.title);
  const [routeDescription, setRouteDescription] = useState(walk.routeDescription);
  const [captureDate, setCaptureDate] = useState(walk.captureDate);
  const [tagsInput, setTagsInput] = useState((walk.tags ?? []).join(', '));
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isReplacingVideo, setIsReplacingVideo] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [videoRotationDegrees, setVideoRotationDegrees] = useState<VideoRotationDegrees>(
    normalizeVideoRotationDegrees(walk.videoRotationDegrees),
  );
  const videoInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const locationBadges = resolveTaggedRetailerLocations(
    walk.instoreLocationIds,
    retailerInstoreLocations,
  );

  useEffect(() => {
    if (!isEditing) {
      setTitle(walk.title);
      setRouteDescription(walk.routeDescription);
      setCaptureDate(walk.captureDate);
      setTagsInput((walk.tags ?? []).join(', '));
      setVideoRotationDegrees(normalizeVideoRotationDegrees(walk.videoRotationDegrees));
    }
  }, [walk, isEditing]);

  if (walkthroughId == null) {
    return (
      <p className="text-sm text-gray-500">
        Demo walk-through — upload a video above to create an editable record for this store.
      </p>
    );
  }

  const handleSave = async () => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      toast({ title: 'Title required', variant: 'destructive' });
      return;
    }

    setIsSaving(true);
    try {
      const parsedTags = tagsInput.split(',').map((tag) => tag.trim()).filter(Boolean);
      const response = await apiRequest(
        `/api/store-profiles/${storeId}/walkthroughs/${walkthroughId}`,
        'PATCH',
        {
          title: trimmedTitle,
          routeDescription: routeDescription.trim() || null,
          captureDate,
          tags: parsedTags,
        },
      );
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Failed to update walk-through');
      }

      await invalidateWalkthroughQueries(queryClient, storeIdParam);
      setIsEditing(false);
      onUpdated?.();
      toast({ title: 'Walk-through updated' });
    } catch (error) {
      toast({
        title: 'Update failed',
        description: error instanceof Error ? error.message : 'Could not save changes',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${walk.title}"? This cannot be undone.`)) return;

    setIsDeleting(true);
    try {
      const response = await apiRequest(
        `/api/store-profiles/${storeId}/walkthroughs/${walkthroughId}`,
        'DELETE',
      );
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Delete failed');
      }

      await invalidateWalkthroughQueries(queryClient, storeIdParam);
      onDeleted?.();
      toast({ title: 'Walk-through deleted' });
    } catch (error) {
      toast({
        title: 'Delete failed',
        description: error instanceof Error ? error.message : 'Could not delete walk-through',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const saveRotation = async (nextRotation: VideoRotationDegrees) => {
    const previous = videoRotationDegrees;
    setIsRotating(true);
    setVideoRotationDegrees(nextRotation);
    onRotationChange?.(nextRotation);
    try {
      const response = await apiRequest(
        `/api/store-profiles/${storeId}/walkthroughs/${walkthroughId}`,
        'PATCH',
        { videoRotationDegrees: nextRotation },
      );
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Failed to update video rotation');
      }
      await invalidateWalkthroughQueries(queryClient, storeIdParam);
      onUpdated?.();
    } catch (error) {
      setVideoRotationDegrees(previous);
      onRotationChange?.(previous);
      toast({
        title: 'Rotation not saved',
        description: error instanceof Error ? error.message : 'Could not save rotation',
        variant: 'destructive',
      });
    } finally {
      setIsRotating(false);
    }
  };

  const handleRotate = (delta: 90 | -90) => {
    void saveRotation(nextVideoRotationDegrees(videoRotationDegrees, delta));
  };

  const rotationControls = (
    <div className="space-y-2">
      <Label>Video orientation</Label>
      <p className="text-xs text-gray-500">
        iPhone videos often ignore rotation metadata in Chrome. Use these if playback looks sideways or upside down.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={isRotating}
          onClick={() => handleRotate(-90)}
        >
          {isRotating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-1" />}
          Rotate left
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={isRotating}
          onClick={() => handleRotate(90)}
        >
          {isRotating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RotateCw className="h-4 w-4 mr-1" />}
          Rotate right
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={isRotating || videoRotationDegrees === 180}
          onClick={() => void saveRotation(180)}
        >
          Fix upside down
        </Button>
        {videoRotationDegrees !== 0 && (
          <Badge variant="outline">{videoRotationDegrees}° rotation applied</Badge>
        )}
      </div>
    </div>
  );

  const handleReplaceVideo = async (file: File) => {
    if (!file.type.startsWith('video/')) {
      toast({ title: 'Invalid file', description: 'Choose an MP4, WebM, or MOV video.', variant: 'destructive' });
      return;
    }

    setIsReplacingVideo(true);
    try {
      const rotationDegrees = await detectVideoRotationFromFile(file).catch(() => 0 as const);
      const thumbnailBlob = await captureWalkthroughThumbnailFromFile(file, rotationDegrees);
      const formData = new FormData();
      formData.append('video', file);
      formData.append('videoRotationDegrees', String(rotationDegrees));
      if (thumbnailBlob && thumbnailBlob.size > 0) {
        formData.append(
          'thumbnail',
          new File([thumbnailBlob], 'thumbnail.jpg', { type: 'image/jpeg' }),
        );
      }
      const response = await apiRequest(
        `/api/store-profiles/${storeId}/walkthroughs/${walkthroughId}/replace-video`,
        'POST',
        formData,
      );
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || error.message || 'Video replace failed');
      }

      setVideoRotationDegrees(rotationDegrees);
      onRotationChange?.(rotationDegrees);
      await invalidateWalkthroughQueries(queryClient, storeIdParam);
      onUpdated?.();
      toast({
        title: 'Video replaced',
        description: rotationDegrees !== 0
          ? `Applied ${rotationDegrees}° orientation fix. Use MP4 for best preview in Chrome.`
          : 'Use MP4 for best preview in Chrome.',
      });
    } catch (error) {
      toast({
        title: 'Video replace failed',
        description: error instanceof Error ? error.message : 'Could not replace video',
        variant: 'destructive',
      });
    } finally {
      setIsReplacingVideo(false);
    }
  };

  if (!isEditing) {
    return (
      <Card className="border-dashed">
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <CardTitle className="text-sm">Walk-through record</CardTitle>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                <Pencil className="h-4 w-4 mr-1" />
                Edit
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="text-red-700 border-red-200"
                disabled={isDeleting}
                onClick={handleDelete}
              >
                {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
                Delete
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <p><span className="text-gray-500">Title:</span> {walk.title}</p>
          <p><span className="text-gray-500">Route:</span> {walk.routeDescription || '—'}</p>
          <p><span className="text-gray-500">Capture date:</span> {walk.captureDate}</p>
          <p><span className="text-gray-500">Captured by:</span> {walk.capturedBy}</p>
          <div className="flex flex-wrap gap-1">
            {locationBadges.length === 0 ? (
              <span className="text-gray-500">No locations tagged</span>
            ) : (
              locationBadges.map((item) => (
                <Badge key={item.id} variant="outline">{item.label}</Badge>
              ))
            )}
          </div>
          {(walk.tags ?? []).length > 0 && (
            <div className="flex flex-wrap gap-1">
              {walk.tags!.map((tag) => (
                <Badge key={tag} variant="secondary">{tag}</Badge>
              ))}
            </div>
          )}
          {videoRotationDegrees !== 0 && (
            <p className="text-xs text-gray-500">{videoRotationDegrees}° orientation applied</p>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-blue-200 bg-blue-50/30">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Edit walk-through</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor={`walk-edit-title-${walkthroughId}`}>Title</Label>
            <Input
              id={`walk-edit-title-${walkthroughId}`}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`walk-edit-date-${walkthroughId}`}>Capture date</Label>
            <Input
              id={`walk-edit-date-${walkthroughId}`}
              type="date"
              value={captureDate}
              onChange={(event) => setCaptureDate(event.target.value)}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor={`walk-edit-route-${walkthroughId}`}>Route description</Label>
          <Input
            id={`walk-edit-route-${walkthroughId}`}
            value={routeDescription}
            onChange={(event) => setRouteDescription(event.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor={`walk-edit-tags-${walkthroughId}`}>Tags</Label>
          <Input
            id={`walk-edit-tags-${walkthroughId}`}
            placeholder="audit, baseline"
            value={tagsInput}
            onChange={(event) => setTagsInput(event.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label>Replace video</Label>
          <input
            ref={videoInputRef}
            type="file"
            accept={VIDEO_ACCEPT}
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (file) void handleReplaceVideo(file);
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isReplacingVideo}
            onClick={() => videoInputRef.current?.click()}
          >
            {isReplacingVideo ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Video className="h-4 w-4 mr-1" />
            )}
            Replace video file
          </Button>
        </div>

        {rotationControls}

        <div className="flex gap-2">
          <Button type="button" size="sm" disabled={isSaving} onClick={handleSave}>
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save changes'}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => setIsEditing(false)}
          >
            <X className="h-4 w-4 mr-1" />
            Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
